import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const MALA = 108;
const MUTE_LS = "dharmartha_japa_muted";
const readMuted = () => { try { return localStorage.getItem(MUTE_LS) === "1"; } catch { return false; } };
const SIZE = 300;
const C = SIZE / 2;
const R = 128;

// Temple-bell (ghanta) tones via WebAudio — no external assets, works offline.
let _ac = null;
function ac() {
  if (typeof window === "undefined") return null;
  try { _ac = _ac || new (window.AudioContext || window.webkitAudioContext)(); return _ac; } catch { return null; }
}
// A struck temple bell has a low hum, a strong strike tone and several
// INHARMONIC overtones that decay at different rates — that shimmering,
// long-ringing "ghanta" sound. We model the classic partial ratios.
function bellStrike(base = 466, gain = 0.2) {
  const a = ac(); if (!a) return;
  if (a.state === "suspended") a.resume();
  const t0 = a.currentTime;
  const partials = [
    [0.5, 0.32, 3.2],  // hum (octave below)
    [1.0, 1.00, 2.9],  // strike / prime
    [1.19, 0.5, 2.2],  // minor-third tierce
    [2.76, 0.55, 1.7], // nominal
    [5.40, 0.3, 1.0],
    [8.93, 0.18, 0.7],
    [13.3, 0.1, 0.5],
  ];
  partials.forEach(([mult, amp, dur]) => {
    const osc = a.createOscillator(); const g = a.createGain();
    osc.type = "sine"; osc.frequency.value = base * mult;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain * amp, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(a.destination);
    osc.start(t0); osc.stop(t0 + dur + 0.05);
  });
}
// Soft high "ting" for each bead; full temple bell on mala completion.
const tick = () => {
  const a = ac(); if (!a) return; if (a.state === "suspended") a.resume();
  const t0 = a.currentTime;
  const o = a.createOscillator(); const g = a.createGain();
  o.type = "sine"; o.frequency.value = 1320;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.045, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.26);
  o.connect(g); g.connect(a.destination);
  o.start(t0); o.stop(t0 + 0.3);
};
const bell = () => bellStrike(466, 0.2);

// A circular japa mala: 108 beads strung in a ring with a meru (guru) bead at the
// top. Beads fill with gold as you chant; the current bead glows. The centre shows
// the live count from 1 to 108. Tap anywhere on the mala to count.
export default function JapaMala({ filled = 0, malaNumber = 0, total = 0, onCount, hint }) {
  const reduce = useReducedMotion();
  const beads = Array.from({ length: MALA });
  const [celebrate, setCelebrate] = useState(false);
  const [muted, setMuted] = useState(readMuted);
  const prev = useRef(filled);

  const toggleMute = () => setMuted((m) => { const n = !m; try { localStorage.setItem(MUTE_LS, n ? "1" : "0"); } catch {} return n; });

  useEffect(() => {
    if (prev.current !== 108 && filled === 108) {
      if (!muted) bell();
      setCelebrate(true);
      const id = setTimeout(() => setCelebrate(false), 1900);
      prev.current = filled;
      return () => clearTimeout(id);
    }
    prev.current = filled;
  }, [filled, muted]);

  const handleTap = () => {
    if (!muted) { if (filled === 107) bell(); else tick(); }
    onCount && onCount();
  };

  return (
    <div className="mt-8 flex w-full max-w-md select-none flex-col items-center">
      <button
        type="button"
        data-testid="japa-sound-toggle"
        onClick={toggleMute}
        aria-pressed={!muted}
        title={muted ? "Sound off" : "Sound on"}
        className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1 text-xs text-ink-3 transition-colors hover:text-saffron"
      >
        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        {muted ? "Bell off" : "Bell on"}
      </button>
      <motion.button
        type="button"
        data-testid="japa-count-button"
        onClick={handleTap}
        whileTap={reduce ? undefined : { scale: 0.97 }}
        style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
        className="group relative aspect-square w-full max-w-[340px]"
      >
        {/* ambient glow */}
        <div className="pointer-events-none absolute inset-6 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(199,80,0,0.14), rgba(198,138,0,0.06) 55%, transparent 72%)", filter: "blur(6px)" }} />

        {/* celebration burst on mala completion */}
        <AnimatePresence>
          {celebrate && !reduce && (
            <>
              <motion.div key="ring" className="pointer-events-none absolute inset-0 rounded-full border-2 border-[var(--gold)]"
                initial={{ scale: 0.6, opacity: 0.8 }} animate={{ scale: 1.25, opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 1.1, ease: "easeOut" }} />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i / 12) * Math.PI * 2;
                return (
                  <motion.span key={i} className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full bg-[var(--gold)]"
                    initial={{ x: 0, y: 0, opacity: 1 }}
                    animate={{ x: Math.cos(a) * 120, y: Math.sin(a) * 120, opacity: 0 }}
                    transition={{ duration: 1.1, ease: "easeOut" }} />
                );
              })}
            </>
          )}
        </AnimatePresence>

        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="relative block h-full w-full" role="img" aria-label="Japa mala">
          <circle cx={C} cy={C} r={R} fill="none" stroke="var(--line)" strokeWidth="1" />
          {beads.map((_, i) => {
            const ang = (-90 + (360 / MALA) * i) * (Math.PI / 180);
            const x = C + R * Math.cos(ang);
            const y = C + R * Math.sin(ang);
            const isMeru = i === 0;
            const isFilled = i < filled;
            const isCurrent = i === filled - 1;
            const rr = isMeru ? 6 : isCurrent ? 5.2 : 3.4;
            const fill = isMeru
              ? "var(--saffron)"
              : isFilled
                ? "var(--gold)"
                : "color-mix(in srgb, var(--ink) 18%, transparent)";
            return (
              <g key={i}>
                {isCurrent && !reduce && (
                  <circle cx={x} cy={y} r="9" fill="var(--gold)" opacity="0.25">
                    <animate attributeName="r" values="7;11;7" dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.3;0.08;0.3" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle cx={x} cy={y} r={rr} fill={fill}
                  style={isFilled && !isMeru ? { filter: "drop-shadow(0 0 3px rgba(184,134,43,0.6))" } : undefined} />
              </g>
            );
          })}
        </svg>

        {/* centre count */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <motion.span key={filled} initial={reduce ? false : { scale: 0.7, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 420, damping: 22 }}
            className="font-serif-latin text-[4.5rem] leading-none text-ink" data-testid="japa-bead-count">
            {filled}
          </motion.span>
          <span className="mt-1 text-xs uppercase tracking-[0.2em] text-ink-3">of 108</span>
          {malaNumber > 0 && (
            <span className="mt-2 rounded-full border border-[var(--gold-muted)]/50 bg-[rgba(184,134,43,0.08)] px-2.5 py-0.5 text-[11px] text-gold">
              {malaNumber} {malaNumber === 1 ? "mala" : "malas"} done
            </span>
          )}
        </div>
      </motion.button>

      {hint && <p className="mt-4 text-xs text-ink-3">{hint}</p>}
    </div>
  );
}
